/* eslint-disable no-nested-ternary */
import {
  AppstoreOutlined,
  BarcodeOutlined,
  BgColorsOutlined,
  CameraOutlined,
  ExpandOutlined,
  GiftOutlined,
  MessageOutlined,
  TagOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { useCompositionButton } from 'hooks/useCompositionButton';
import { noop } from 'lodash';
import { useMemo } from 'react';

import { Badge } from 'antd';
import SelectPacking from 'components/BusinessLogic/Product/SelectPacking';
import ProductCardImage from 'components/UI/ProductCardImage';
import { RealPhotosLegend } from 'components/UI/ProductCardImageSimple.styled';
import Quality from 'components/UI/Quality';
import { OfferInfo, ProductCardActions, ProductHeader } from '../../molecules';
import {
  ActionsSection,
  ContentSection,
  DetailsRow,
  HeaderRow,
  ImageSection,
  PackingSelectWrapper,
  ProductCardListDesktopCard,
  ProductCardListDesktopContainer,
} from './ProductCardListDesktop.styled';
import ProductDetailsRow from './ProductDetailRow';

import { ProductCardListProps } from './types';

export function ProductCardListDesktop({
  product,
  packingSelected,
  onDetailsClick,
  buy,
  disabled = false,
  addInCart,
  selectedTypeSale,
  showModalComposition,
  onSelectPacking,
  isExclusiveOffer = false,
}: ProductCardListProps) {
  const canShowComposition = useCompositionButton(product);

  const { offerType } = product;
  const isEnpOffer = useMemo(
    () => offerType === 0 || offerType === 3,
    [offerType],
  );

  const isLkpOffer = useMemo(
    () => offerType === 1 || offerType === 2,
    [offerType],
  );

  const details = [
    {
      icon: <TrophyOutlined />,
      label: 'Qualidade:',
      value: <Quality quality={product.quality as any} />,
    },
    {
      icon: <GiftOutlined />,
      label: 'Embalagem:',
      value: (
        <PackingSelectWrapper>
          <SelectPacking
            offerId={product.offerId}
            onSelect={onSelectPacking || noop}
            packings={product.packings}
            value={packingSelected?.id || 0}
            type="small"
          />
        </PackingSelectWrapper>
      ),
    },
    {
      icon: <ExpandOutlined />,
      label: 'Dimensão:',
      value: product.dimension || 'N/A',
    },
    {
      icon: <AppstoreOutlined />,
      label: 'Múltiplo:',
      value: packingSelected?.multiple || 'N/A',
    },
    {
      icon: <BgColorsOutlined />,
      label: 'Cores:',
      value: product.colors || 'N/A',
    },
    {
      icon: <TagOutlined />,
      label: 'Cód. do Produto:',
      value: product.barcode || 'N/A',
      enableCopy: true,
    },
    {
      icon: <BarcodeOutlined />,
      label: 'Cod. de Barras:',
      value: product.gtincode || 'N/A',
      enableCopy: true,
    },
  ];

  const observationDetails =
    product.observation && product.observation.trim() !== ''
      ? [
          {
            icon: <MessageOutlined />,
            label: 'Observação:',
            value: product.observation,
          },
        ]
      : [];

  const isDirectedOffer = product.offerType === 3;
  const hasSpecialPrice =
    !Number.isNaN(product.specialPrice) && product.specialPrice > 0;
  const hasValidReajustment =
    !Number.isNaN(product.reajustmentAmount) && product.reajustmentAmount !== 0;

  const shouldShowBadge = isDirectedOffer
    ? hasSpecialPrice
    : hasValidReajustment;

  let badgeText = '';
  if (isDirectedOffer && hasSpecialPrice) {
    badgeText = `Oferta direcionada R$${Math.abs(product.specialPrice)
      .toFixed(2)
      .replace('.', ',')}`;
  } else if (product.voucherReajustmentType === 'P') {
    badgeText = `${Math.abs(product.reajustmentAmount)}%`;
  } else {
    badgeText = `R$${Math.abs(product.reajustmentAmount)
      .toFixed(2)
      .replace('.', ',')}`;
  }

  return (
    <ProductCardListDesktopContainer className="product-card-list-desktop-container">
      <Badge.Ribbon
        text={badgeText}
        placement="start"
        color="green"
        style={{ display: shouldShowBadge ? 'block' : 'none' }}
      >
        <ProductCardListDesktopCard>
          <ImageSection data-grid="image">
            {product.imagesFormatted?.[0] && (
              <ProductCardImage
                images={product.imagesFormatted}
                alt={`Imagem de ${product.longName}`}
                isLkpOffer={isLkpOffer}
                showNavigation
                showLabel={false}
                productName={product.longName}
              />
            )}
          </ImageSection>
          <ContentSection data-grid="content">
            <HeaderRow>
              <ProductHeader
                product={product}
                isExclusiveOffer={isExclusiveOffer}
              />
              <OfferInfo product={product} />
            </HeaderRow>

            <DetailsRow>
              <ProductDetailsRow details={details} />
              <ProductDetailsRow details={observationDetails} />
            </DetailsRow>

            {isLkpOffer &&
              (Array.isArray(product.gfpImages)
                ? product.gfpImages.length > 0
                : !!product.gfpImages) && (
                <RealPhotosLegend>
                  <CameraOutlined />
                  Oferta com fotos reais. Veja nos detalhes (GFPs).
                </RealPhotosLegend>
              )}
          </ContentSection>

          <ActionsSection data-grid="actions">
            <ProductCardActions
              product={product}
              packingSelected={packingSelected}
              disabled={disabled}
              disableBuyButton={isEnpOffer}
              onShowDetails={onDetailsClick || noop}
              onBuyNow={(quantity, saleType) =>
                buy ? buy(quantity, saleType) : undefined
              }
              onAddToCart={(quantity, saleType) =>
                addInCart ? addInCart(quantity, saleType) : undefined
              }
              onComposition={showModalComposition}
              showCompositionButton={canShowComposition}
            />
          </ActionsSection>
        </ProductCardListDesktopCard>
      </Badge.Ribbon>
    </ProductCardListDesktopContainer>
  );
}
